import React, {useContext, useEffect, useState} from "react"
import {useServices} from "../../../services/services"
import {TransactionsWithCount} from "../../../domain/transactions-with-count"
import {getApiCallErrorMessageHandler} from "../../../services/api/api-utils"
import {CategoryHelper, CategoryHelperContext} from "../../../services/helpers/category-helper"
import {defaultSortDesc, getInitialTransactionsSettings, SortOn, SortOptions, TransactionsSettings} from "../../../services/api/transactions-api"
import {ResetCategoryScope, TransactionsView} from "./transactions-view"
import {Transaction} from "../../../domain/transaction"
import {TransactionModal} from "./transaction-modal/transaction-modal"


interface TransactionsProps {
  categoryId?: number
  setCategoryId: (categoryId: number) => void
  from: Date | null
  until: Date | null
  scopeDisplay: string | null
  refreshSpendingData: () => void
  impactFirst?: boolean
  showFilter: boolean
  resetCategoryScope?: ResetCategoryScope
  doNotShowIncome?: boolean
}

export const Transactions : React.FC<TransactionsProps> = ({ categoryId, setCategoryId, from, until, scopeDisplay, refreshSpendingData, impactFirst, showFilter, resetCategoryScope, doNotShowIncome }) => {
  const categoryHelper = useContext<CategoryHelper | null>(CategoryHelperContext)
  const [transactionsWithCount, setTransactionsWithCount] = useState<TransactionsWithCount | null>(null)
  const [transactionsSettings, setTransactionsSettings] = useState<TransactionsSettings | null>(null)
  const services = useServices()
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null)
  const controller = new AbortController()

  useEffect(() => {
    // if the input is changed, the settings should be overwritten
    setTransactionsSettings({...getInitialTransactionsSettings(), categoryId, dateRange: {type: "custom", from: from ?? undefined, until: until ?? undefined}})
  }, [categoryId, from, until])

  useEffect(() => {
    if (transactionsSettings !== null && ((transactionsSettings.sortOn.sort === "amount" && impactFirst) || (transactionsSettings.sortOn.sort === "impact" && !impactFirst))) {
      const newTransactionsSettings: TransactionsSettings = {...transactionsSettings, sortOn: {...transactionsSettings.sortOn, sort: impactFirst ? "impact" : "amount"}}
      setTransactionsSettings(newTransactionsSettings)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [impactFirst])

  const loadTransactions = async () => {
    if (services === null || transactionsSettings === null || categoryHelper === null) return

    const apiCallErrorHandler = getApiCallErrorMessageHandler()

    try {
      const transactionsWithCount = await services.transactionsApi.getTransactions(transactionsSettings, controller.signal)

      // temporary; this should be solved in the backend and switched off categories should also be taken into account
      if (doNotShowIncome) {
        const incomeCategoryIds = categoryHelper.categories.filter(x => x.isIncome).map(x => x.id)
        transactionsWithCount.transactions = transactionsWithCount.transactions.filter(x => {
          return incomeCategoryIds.indexOf(x.categoryId) === -1
        })
      }

      setTransactionsWithCount(transactionsWithCount)
    } catch (err) {
      apiCallErrorHandler(err)
    }
  }

  useEffect(() => {
    if (services === null || transactionsSettings === null) return

    // noinspection JSIgnoredPromiseFromCall
    loadTransactions()

    return () => controller.abort()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transactionsSettings, services])

  if (transactionsSettings === null || transactionsWithCount === null || categoryHelper === null) return null


  const changeSort = (sort: SortOptions) => {
    const setSortOn: (sortOn: SortOn) => void = (sortOn) => {
      setTransactionsSettings({...transactionsSettings, sortOn})
    }

    if (transactionsSettings.sortOn.sort === sort) {
      setSortOn({...transactionsSettings.sortOn, sortDesc: !transactionsSettings.sortOn.sortDesc})
    } else {
      setSortOn({sort, sortDesc: defaultSortDesc[sort]})
    }
  }

  const showAll = () => {
    setTransactionsSettings({...transactionsSettings, unlimitedTransactions: true})
  }

  const clickTransaction = (transaction: Transaction) => {
    setSelectedTransaction(transaction)
  }

  const closeTransactionModalAndReload = () => {
    loadTransactions()
    refreshSpendingData()
    closeTransactionModal()
  }

  const closeTransactionModal = () => {
    setSelectedTransaction(null)
  }

  return <>
    <TransactionsView setCategoryId={setCategoryId} scopeDisplay={scopeDisplay} transactionsSettings={transactionsSettings} setTransactionsSettings={setTransactionsSettings} transactionsWithCount={transactionsWithCount} changeSort={changeSort} showAll={showAll} clickTransaction={clickTransaction} impactFirst={impactFirst} showFilter={showFilter} resetCategoryScope={resetCategoryScope}/>
    <TransactionModal transaction={selectedTransaction} closeModal={closeTransactionModal} saveAndCloseModal={closeTransactionModalAndReload}/>
  </>
}
